import { useEffect, useRef } from 'react';

const Arrow = ({ flip }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={flip ? { transform: 'rotate(180deg)' } : undefined}>
    <path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function CaseStudy({ project, index, total, onClose, onNav }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNav(1);
      if (e.key === 'ArrowLeft') onNav(-1);
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, onNav]);

  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [project]);

  if (!project) return null;
  const s = project.study;

  return (
    <div className="cs-root" role="dialog" aria-modal="true" aria-label={`${project.title} case study`}>
      <div className="cs-backdrop" onClick={onClose} />
      <div className="cs-panel" ref={panelRef} tabIndex={-1}>
        <header className="cs-header">
          <div className="cs-header-meta">
            <span className="project-tag-pf">{project.tag}</span>
            <span className="project-year-pf">{project.year}</span>
            <span className="cs-counter">{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          </div>
          <button className="cs-close" onClick={onClose} aria-label="Close case study">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            <span>Esc</span>
          </button>
        </header>

        <div className="cs-body">
          <aside className="cs-side">
            <h2 className="cs-title">{project.title}</h2>
            <p className="cs-lede">{project.desc}</p>

            <dl className="cs-facts">
              <div><dt>Role</dt><dd>{s.role}</dd></div>
              <div><dt>Timeline</dt><dd>{s.timeline}</dd></div>
              <div>
                <dt>Stack</dt>
                <dd className="cs-stack">{project.stack.map((t, i) => <span key={i} className="stack-item-pf">{t}</span>)}</dd>
              </div>
            </dl>

            <div className="cs-links">
              {project.demo && (
                <a href={project.demo} target="_blank" rel="noreferrer" className="project-link-btn accent">
                  View live <Arrow />
                </a>
              )}
              {project.gh && (
                <a href={project.gh} target="_blank" rel="noreferrer" className="project-link-btn">GitHub</a>
              )}
            </div>
          </aside>

          <main className="cs-main">
            {project.img && (
              <div className="cs-hero-img">
                <img src={project.img} alt={project.title} />
              </div>
            )}

            <section className="cs-section">
              <div className="cs-step"><span>01</span> Problem</div>
              <p>{s.problem}</p>
            </section>

            <section className="cs-section">
              <div className="cs-step"><span>02</span> Constraints</div>
              <ul className="cs-list">
                {s.constraints.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </section>

            <section className="cs-section">
              <div className="cs-step"><span>03</span> Decision</div>
              <p>{s.decision}</p>
            </section>

            <section className="cs-section">
              <div className="cs-step"><span>04</span> Result</div>
              <div className="cs-metrics">
                {s.results.map((m, i) => (
                  <div key={i} className="cs-metric">
                    <div className="cs-metric-num">{m.num}</div>
                    <div className="cs-metric-label">{m.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="cs-section cs-tradeoff">
              <div className="cs-step"><span>&para;</span> What I'd revisit</div>
              <p>{s.tradeoff}</p>
            </section>
          </main>
        </div>

        <footer className="cs-footer">
          <button className="cs-nav" onClick={() => onNav(-1)}>
            <Arrow flip /> Previous
          </button>
          <button className="cs-nav" onClick={() => onNav(1)}>
            Next project <Arrow />
          </button>
        </footer>
      </div>
    </div>
  );
}

export default CaseStudy;
